import { type ComponentType } from 'react';

import { type TypeMenuState } from '@thefarmersfront/kitchen';

import ClaimExchangePage from '~/pages/claim/exchange/ClaimExchangePage';
import ClaimReturnPage from '~/pages/claim/return/ClaimReturnPage';
import DeliveryAgencyOrderPage from '~/pages/delivery-agency/order/DeliveryAgencyOrderPage';
import DeliveryAgencyOutboundUnitPage from '~/pages/delivery-agency/outbound-unit/DeliveryAgencyOutboundUnitPage';
import DeliveryAgencyStatusPage from '~/pages/delivery-agency/status/DeliveryAgencyStatusPage';
import DeliveryAgencyTrackingPage from '~/pages/delivery-agency/tracking/DeliveryAgencyTrackingPage';
import MainPage from '~/pages/main/MainPage';
import OrderOrdersPage from '~/pages/order/orders/OrderOrdersPage';
import ProductMappingPage from '~/pages/product/mapping/ProductMappingPage';
import ProductOperatingPage from '~/pages/product/operating/ProductOperatingPage';
import ProductRegisterPage from '~/pages/product/register/ProductRegisterPage';
import ReceiveReportPage from '~/pages/receive/report/ReceiveReportPage';
import ReceiveRequestsPage from '~/pages/receive/requests/ReceiveRequestsPage';
import ReceiveStatusPage from '~/pages/receive/status/ReceiveStatusPage';
import SettlementStoragePage from '~/pages/settlement/storage/SettlementStoragePage';
import StockDatePage from '~/pages/stock/date/StockDatePage';
import StockLedgerPage from '~/pages/stock/ledger/StockLedgerPage';
import StockReleasePage from '~/pages/stock/release/StockReleasePage';
import StockSkuPage from '~/pages/stock/sku/StockSkuPage';
import VenderConfirmPage from '~/pages/vender/confirm/VenderConfirmPage';
import VenderModifyPage from '~/pages/vender/modify/VenderModifyPage';
import VenderSupplierPage from '~/pages/vender/supplier/VenderSupplierPage';

import { type TypePermissions, checkPermission } from '~/utils/auth';

import { type TypeLogisticsType, type TypeOwnerType } from '~/constants/common';
import { PATH } from '~/constants/path';

type TypeRouteItem = {
  path: string;
  title: string;
  component: ComponentType;
  permissions: TypePermissions;
  parentKey?: string;
  hideInSidebar?: boolean;
};

// 통합 라우트 설정
export const ROUTE_CONFIG: TypeRouteItem[] = [
  // 메인
  {
    path: PATH.MAIN,
    title: '메인',
    component: MainPage,
    permissions: [{ ownerType: 'FBK' }, { ownerType: '3PL' }, { ownerType: 'NFA' }],
  },

  // 상품 관리
  {
    path: PATH.PRODUCT_REGISTER,
    title: '상품 등록/수정',
    component: ProductRegisterPage,
    permissions: [{ ownerType: 'FBK' }, { ownerType: '3PL', logisticsTypes: ['FULFILLMENT'] }],
    parentKey: PATH.PRODUCT,
  },
  {
    path: PATH.PRODUCT_OPERATING,
    title: '운영 상품 조회',
    component: ProductOperatingPage,
    permissions: [{ ownerType: 'FBK' }, { ownerType: '3PL', logisticsTypes: ['FULFILLMENT'] }],
    parentKey: PATH.PRODUCT,
  },
  {
    path: PATH.PRODUCT_MAPPING,
    title: '상품 판매처 매핑',
    component: ProductMappingPage,
    permissions: [{ ownerType: '3PL', logisticsTypes: ['FULFILLMENT'] }],
    parentKey: PATH.PRODUCT,
  },

  // 입고 관리
  {
    path: PATH.RECEIVE_REQUESTS,
    title: '입고 요청 관리',
    component: ReceiveRequestsPage,
    permissions: [
      { ownerType: 'FBK' },
      { ownerType: '3PL', logisticsTypes: ['FULFILLMENT'] },
      { ownerType: 'NFA', logisticsTypes: ['FULFILLMENT'] },
    ],
    parentKey: PATH.RECEIVE,
  },
  {
    path: PATH.RECEIVE_STATUS,
    title: '입고 현황',
    component: ReceiveStatusPage,
    permissions: [
      { ownerType: 'FBK' },
      { ownerType: '3PL', logisticsTypes: ['FULFILLMENT'] },
      { ownerType: 'NFA', logisticsTypes: ['FULFILLMENT'] },
    ],
    parentKey: PATH.RECEIVE,
  },
  {
    path: PATH.RECEIVE_REPORT,
    title: '입고 리포트 관리',
    component: ReceiveReportPage,
    permissions: [{ ownerType: '3PL', logisticsTypes: ['FULFILLMENT'] }],
    parentKey: PATH.RECEIVE,
  },

  // 재고 관리
  {
    path: PATH.STOCK_SKU,
    title: '상품별 조회',
    component: StockSkuPage,
    permissions: [{ ownerType: 'FBK' }, { ownerType: '3PL', logisticsTypes: ['FULFILLMENT'] }],
    parentKey: PATH.STOCK,
  },
  {
    path: PATH.STOCK_DATE,
    title: '유통기한별 조회',
    component: StockDatePage,
    permissions: [{ ownerType: 'FBK' }, { ownerType: '3PL', logisticsTypes: ['FULFILLMENT'] }],
    parentKey: PATH.STOCK,
  },
  {
    path: PATH.STOCK_RELEASE,
    title: '반출 요청 관리',
    component: StockReleasePage,
    permissions: [{ ownerType: 'FBK' }, { ownerType: '3PL', logisticsTypes: ['FULFILLMENT'] }],
    parentKey: PATH.STOCK,
  },
  {
    path: PATH.STOCK_LEDGER,
    title: '재고수불부',
    component: StockLedgerPage,
    permissions: [{ ownerType: 'FBK' }, { ownerType: '3PL', logisticsTypes: ['FULFILLMENT'] }],
    parentKey: PATH.STOCK,
  },

  // 정산 관리
  {
    path: PATH.SETTLEMENT_STORAGE,
    title: '보관비 조회',
    component: SettlementStoragePage,
    permissions: [{ ownerType: 'FBK' }],
    parentKey: PATH.SETTLEMENT,
  },

  // 출고 주문 관리
  {
    path: PATH.ORDER_ORDERS,
    title: '출고 주문 조회',
    component: OrderOrdersPage,
    permissions: [
      { ownerType: 'FBK' },
      { ownerType: '3PL', logisticsTypes: ['FULFILLMENT'] },
      { ownerType: 'NFA', logisticsTypes: ['FULFILLMENT'] },
    ],
    parentKey: PATH.ORDER,
  },

  // 반품 교환 관리
  {
    path: PATH.CLAIM_RETURN,
    title: '반품 신청/조회',
    component: ClaimReturnPage,
    permissions: [
      { ownerType: '3PL', logisticsTypes: ['FULFILLMENT'] },
      { ownerType: 'NFA', logisticsTypes: ['FULFILLMENT'] },
    ],
    parentKey: PATH.CLAIM,
  },
  {
    path: PATH.CLAIM_EXCHANGE,
    title: '교환 신청/조회',
    component: ClaimExchangePage,
    permissions: [
      { ownerType: '3PL', logisticsTypes: ['FULFILLMENT'] },
      { ownerType: 'NFA', logisticsTypes: ['FULFILLMENT'] },
    ],
    parentKey: PATH.CLAIM,
  },

  // 배송 관리
  {
    path: PATH.DELIVERY_AGENCY_STATUS,
    title: '배송 현황',
    component: DeliveryAgencyStatusPage,
    permissions: [
      { ownerType: '3PL', logisticsTypes: ['DELIVERY'] },
      { ownerType: 'NFA', logisticsTypes: ['DELIVERY'] },
    ],
    parentKey: PATH.DELIVERY_AGENCY,
  },
  {
    path: PATH.DELIVERY_AGENCY_ORDER,
    title: '배송 주문 관리',
    component: DeliveryAgencyOrderPage,
    permissions: [
      { ownerType: '3PL', logisticsTypes: ['DELIVERY'] },
      { ownerType: 'NFA', logisticsTypes: ['DELIVERY'] },
    ],
    parentKey: PATH.DELIVERY_AGENCY,
  },
  {
    path: PATH.DELIVERY_AGENCY_TRACKING,
    title: '운송장 관리',
    component: DeliveryAgencyTrackingPage,
    permissions: [
      { ownerType: '3PL', logisticsTypes: ['DELIVERY'] },
      { ownerType: 'NFA', logisticsTypes: ['DELIVERY'] },
    ],
    parentKey: PATH.DELIVERY_AGENCY,
  },
  {
    path: PATH.DELIVERY_AGENCY_OUTBOUND_UNIT,
    title: '합포 단위 관리',
    component: DeliveryAgencyOutboundUnitPage,
    permissions: [
      { ownerType: '3PL', logisticsTypes: ['DELIVERY'] },
      { ownerType: 'NFA', logisticsTypes: ['DELIVERY'] },
    ],
    parentKey: PATH.DELIVERY_AGENCY,
  },

  // 화주사 정보 관리
  {
    path: PATH.VENDER_CONFIRM,
    title: '화주사 정보',
    component: VenderConfirmPage,
    permissions: [{ ownerType: 'FBK' }, { ownerType: '3PL' }, { ownerType: 'NFA' }],
    parentKey: PATH.VENDER,
  },
  {
    path: PATH.VENDER_SUPPLIER,
    title: '공급사 관리',
    component: VenderSupplierPage,
    permissions: [{ ownerType: 'FBK' }, { ownerType: '3PL', logisticsTypes: ['FULFILLMENT'] }],
    parentKey: PATH.VENDER,
  },
  {
    path: PATH.VENDER_MODIFY,
    title: '화주사 정보 수정',
    component: VenderModifyPage,
    permissions: [{ ownerType: 'FBK' }, { ownerType: '3PL' }, { ownerType: 'NFA' }],
    parentKey: PATH.VENDER,
    hideInSidebar: true,
  },
];

// 메뉴 그룹 정의
const MENU_GROUPS: Record<string, string> = {
  [PATH.PRODUCT]: '상품 관리',
  [PATH.RECEIVE]: '입고 관리',
  [PATH.STOCK]: '재고 관리',
  [PATH.SETTLEMENT]: '정산 관리',
  [PATH.ORDER]: '출고 주문 관리',
  [PATH.CLAIM]: '반품 교환 관리',
  [PATH.DELIVERY_AGENCY]: '배송 관리',
  [PATH.VENDER]: '화주사 정보 관리',
};

/**
 * 사용자 권한에 따른 접근 가능한 라우트 반환
 */
export const getAccessibleRoutes = (ownerType: TypeOwnerType, logisticsTypes: TypeLogisticsType[]): TypeRouteItem[] => {
  if (!ownerType) {
    return [];
  }
  return ROUTE_CONFIG.filter((route) => checkPermission(ownerType, logisticsTypes, route.permissions));
};

/**
 * 사용자 권한에 따른 사이드바 메뉴 설정 생성
 */
export const generateSidebarConfig = (
  ownerType: TypeOwnerType,
  logisticsTypes: TypeLogisticsType[]
): TypeMenuState[] => {
  if (!ownerType) {
    return [];
  }
  const accessibleRoutes = getAccessibleRoutes(ownerType, logisticsTypes);
  const menuRecord: Record<string, TypeMenuState> = {};

  accessibleRoutes.forEach((route) => {
    // 메인 페이지와 숨김 항목은 메뉴에서 제외
    if (!route.parentKey || route.hideInSidebar) return;

    const parentKey = route.parentKey;

    // 메뉴 그룹이 없으면 생성
    if (!(parentKey in menuRecord)) {
      menuRecord[parentKey] = {
        title: MENU_GROUPS[parentKey],
        key: parentKey,
      };
    }

    const menu = menuRecord[parentKey];
    if (!menu) return;

    // 서브메뉴 추가
    menu.submenus ??= [];

    menu.submenus.push({
      title: route.title,
      key: route.path,
    });
  });

  return Object.values(menuRecord);
};

/**
 * 사용자 권한에 따른 기본 리다이렉트 경로 반환
 */
export const getDefaultRoute = (ownerType: TypeOwnerType, logisticsTypes: TypeLogisticsType[]): string => {
  if (!ownerType) {
    return PATH.MAIN;
  }
  const accessibleRoutes = getAccessibleRoutes(ownerType, logisticsTypes);

  const firstMenuRoute = accessibleRoutes.find((route) => route.parentKey && !route.hideInSidebar);

  return firstMenuRoute ? firstMenuRoute.path : PATH.MAIN;
};
